import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, CheckCircle2 } from "lucide-react";

import {
  PageEyebrowLink,
  PageHeader,
  Button,
  Checkbox,
  Label,
  Textarea,
} from "@/components/ui";
import { useToast } from "@/hooks";
import {
  submitDataDeletionRequest,
  type DataDeletionRequestResponse,
} from "@/services/data-deletion-requests";
import { DisplayError } from "@/utils";

/**
 * LGPD self-service data deletion request form.
 *
 * Posts to /api/v1/data-deletion-requests with the authenticated user's
 * context (resolved server-side from the auth + tenancy middleware).
 * On success, shows the protocol id; on failure, shows the explicit
 * server message.
 */
export function DataDeletionRequest() {
  const { toast } = useToast();
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<DataDeletionRequestResponse | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!confirmed || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const response = await submitDataDeletionRequest({
        reason_text: reason.trim() || undefined,
        confirmation: true,
      });
      setSuccess(response);
      toast({
        title: "Solicitação registrada",
        description: `Protocolo: ${response.data.id}`,
      });
    } catch (e) {
      const msg = DisplayError(e);
      setError(msg);
      toast({
        title: "Não foi possível registrar a solicitação",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <section className="space-y-7">
        <Header />
        <div className="rounded-xl border border-green-200 bg-green-50 p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-6 w-6 flex-shrink-0 text-green-600" />
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-green-900">
                Solicitação registrada
              </h2>
              <p className="text-sm text-green-800">
                Recebemos sua solicitação de exclusão de dados. Entraremos em
                contato em até 15 dias úteis com a confirmação ou eventuais
                pendências legais que impeçam a exclusão imediata (por
                exemplo, retenção tributária obrigatória).
              </p>
              <p className="text-sm text-green-800">
                <strong>Protocolo:</strong>{" "}
                <code className="rounded bg-white px-2 py-0.5 font-mono text-xs text-green-900">
                  {success.data.id}
                </code>
              </p>
              <p className="text-xs text-green-700">
                Status atual: {success.data.status}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-7">
      <Header />

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="space-y-1 text-sm text-amber-900">
            <p className="font-semibold">Antes de prosseguir</p>
            <p>
              Esta solicitação dará início ao processo de exclusão dos seus
              dados pessoais conforme o artigo 18, VI da LGPD. A exclusão é
              <strong> irreversível</strong>. Dados que devem ser mantidos
              por obrigação legal (ex.: registros fiscais) serão preservados
              apenas pelo prazo necessário e na forma exigida por lei. Para
              detalhes, consulte a{" "}
              <Link
                to="/legal/privacidade"
                className="underline underline-offset-2"
              >
                Política de Privacidade
              </Link>
              .
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="reason">Motivo (opcional)</Label>
          <Textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Conte-nos brevemente por que você está solicitando a exclusão. Isso nos ajuda a melhorar."
            rows={5}
            maxLength={1000}
            disabled={submitting}
          />
          <p className="text-xs text-zinc-500">
            Até 1000 caracteres. Você não é obrigado a justificar.
          </p>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-zinc-200 p-4">
          <Checkbox
            id="confirmation"
            checked={confirmed}
            onCheckedChange={(checked) => setConfirmed(checked === true)}
            disabled={submitting}
            className="mt-0.5"
          />
          <Label
            htmlFor="confirmation"
            className="flex-1 text-sm font-normal text-zinc-700"
          >
            Confirmo que entendi que esta solicitação iniciará o processo de
            exclusão dos meus dados pessoais e que esta ação é
            irreversível.
          </Label>
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-3">
          <Button
            type="submit"
            disabled={!confirmed || submitting}
            isLoading={submitting}
            variant="destructive"
            className="px-8"
          >
            Solicitar exclusão
          </Button>
        </div>
      </form>
    </section>
  );
}

function Header() {
  return (
    <PageHeader
      eyebrow={
        <PageEyebrowLink to="/settings/account">Configurações</PageEyebrowLink>
      }
      title="Exclusão de dados (LGPD)"
      lede="Você pode solicitar a exclusão dos seus dados pessoais a qualquer momento. Após o registro, nossa equipe analisará a solicitação e retornará em até 15 dias úteis."
    />
  );
}
